// components/FeedbackImagePicker.jsx
//
// Screenshots (or any picture) to send with feedback: chosen from disk,
// dropped onto the box, or pasted into the feedback dialog (the dialog hands
// pasted files to the same addImages). The files go up with the feedback
// itself, so nothing here talks to the server.

import AddPhotoAlternateIcon from "@mui/icons-material/AddPhotoAlternate";
import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useRef, useState } from "react";

export const FEEDBACK_IMAGE_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
];
export const FEEDBACK_MAX_IMAGES = 5;
export const FEEDBACK_MAX_IMAGE_BYTES = 10 * 1024 * 1024;

let nextId = 0;

/**
 * The files that can be added to `current`, as picker entries, and a reason
 * for each one that can't.
 */
export const takeImages = (current, files) => {
  const added = [];
  const refused = [];
  for (const file of files) {
    if (!FEEDBACK_IMAGE_TYPES.includes(file.type)) {
      refused.push(`${file.name} isn't a PNG, JPEG, WebP or GIF image.`);
    } else if (file.size > FEEDBACK_MAX_IMAGE_BYTES) {
      refused.push(`${file.name} is larger than 10 MB.`);
    } else if (current.length + added.length >= FEEDBACK_MAX_IMAGES) {
      refused.push(`Attach up to ${FEEDBACK_MAX_IMAGES} images.`);
      break;
    } else {
      added.push({
        id: `feedback-image-${(nextId += 1)}`,
        file,
        url: URL.createObjectURL(file),
      });
    }
  }
  return { added, refused };
};

const FeedbackImagePicker = ({ images, onAdd, onRemove, disabled }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);
  const full = images.length >= FEEDBACK_MAX_IMAGES;

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    if (!disabled) onAdd([...event.dataTransfer.files]);
  };

  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Images (optional): screenshots help us see what you saw. Up to{" "}
        {FEEDBACK_MAX_IMAGES}, 10 MB each.
      </Typography>
      <Stack direction="row" flexWrap="wrap" gap={1}>
        {images.map((image) => (
          <Box
            key={image.id}
            sx={{
              position: "relative",
              width: 96,
              height: 72,
              borderRadius: 1,
              overflow: "hidden",
              border: 1,
              borderColor: "divider",
            }}
          >
            <Box
              component="img"
              src={image.url}
              alt={image.file.name}
              title={image.file.name}
              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
            />
            <IconButton
              size="small"
              aria-label={`Remove ${image.file.name}`}
              onClick={() => onRemove(image.id)}
              disabled={disabled}
              sx={{
                position: "absolute",
                top: 2,
                right: 2,
                p: 0.25,
                bgcolor: "rgba(0, 0, 0, 0.6)",
                color: "#fff",
                "&:hover": { bgcolor: "rgba(0, 0, 0, 0.8)" },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ))}
        {!full && (
          <Tooltip title="Choose, drop or paste (Ctrl/⌘+V) an image">
            <Box
              component="button"
              type="button"
              disabled={disabled}
              onClick={() => inputRef.current?.click()}
              onDragOver={(event) => {
                event.preventDefault();
                setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              sx={{
                width: images.length ? 96 : "100%",
                height: 72,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1,
                borderRadius: 1,
                border: 1,
                borderStyle: "dashed",
                borderColor: dragging ? "primary.main" : "divider",
                bgcolor: dragging ? "action.hover" : "transparent",
                color: "text.secondary",
                cursor: disabled ? "default" : "pointer",
                font: "inherit",
                "&:hover": { borderColor: "primary.main" },
              }}
            >
              <AddPhotoAlternateIcon />
              {!images.length && (
                <Typography variant="body2" component="span">
                  Add images: choose, drop or paste a screenshot
                </Typography>
              )}
            </Box>
          </Tooltip>
        )}
      </Stack>
      <input
        ref={inputRef}
        type="file"
        accept={FEEDBACK_IMAGE_TYPES.join(",")}
        multiple
        hidden
        data-testid="feedback-image-input"
        onChange={(event) => {
          onAdd([...event.target.files]);
          event.target.value = "";
        }}
      />
    </Box>
  );
};

export default FeedbackImagePicker;
